import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardList, Plus, RefreshCw } from 'lucide-react';
import type { FieldEngineer, FieldJob, FieldJobStatus } from '@simpleacs/shared';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import { formatRelative } from '../lib/format';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  PageHeader,
  Pill,
  Spinner,
  Table,
  TBody,
  THead,
  TRow,
  type PillTone,
} from '../components/ui';
import { ChecklistCard } from './field/ChecklistCard';
import { GpsPolicyCard } from './field/GpsPolicyCard';
import { RaiseFieldJobDialog } from './field/RaiseFieldJobDialog';
import { FieldJobDetailDialog } from './field/FieldJobDetailDialog';

const STATUS_TONE: Record<FieldJobStatus, PillTone> = {
  assigned: 'info',
  in_progress: 'primary',
  complete: 'success',
  incomplete: 'warn',
  closed: 'neutral',
  cancelled: 'neutral',
};
const STATUS_LABEL: Record<FieldJobStatus, string> = {
  assigned: 'Assigned',
  in_progress: 'In progress',
  complete: 'Complete',
  incomplete: 'Incomplete',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

const FILTERS: Array<{ key: string; label: string; statuses: FieldJobStatus[] }> = [
  { key: 'open', label: 'Open', statuses: ['assigned', 'in_progress'] },
  { key: 'incomplete', label: 'Needs closing', statuses: ['incomplete'] },
  { key: 'complete', label: 'Complete', statuses: ['complete'] },
  { key: 'closed', label: 'Closed', statuses: ['closed', 'cancelled'] },
  { key: 'all', label: 'All', statuses: [] },
];

/**
 * The dispatcher's board.
 *
 * Raise a deployment job and point it at real equipment, watch it move across
 * the field, reassign or cancel one still open, and confirm-and-close one the
 * field could not finish. The GPS-accuracy policy — the line a fix has to beat
 * before the app will place anything on it — is the operator's own dial, here
 * beside the work it governs. The mobile app is the other half of this; this is
 * where the office stands.
 */
export function FieldJobsPage() {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const canManage = can('field.manage');
  const canClose = can('field.close_incomplete');

  const [filter, setFilter] = useState('open');
  const [raising, setRaising] = useState(false);
  const [openJob, setOpenJob] = useState<FieldJob | null>(null);

  const jobs = useQuery({
    queryKey: ['field', 'jobs'],
    queryFn: () => api.get<{ items: FieldJob[] }>('/field/jobs'),
    refetchInterval: 30_000,
  });
  const engineersQuery = useQuery({
    queryKey: ['field', 'engineers'],
    queryFn: () => api.get<{ items: FieldEngineer[] }>('/field/engineers'),
    enabled: canManage,
  });
  const engineers = engineersQuery.data?.items ?? [];

  const rows = useMemo(() => {
    const items = jobs.data?.items ?? [];
    const chosen = FILTERS.find((f) => f.key === filter) ?? FILTERS[0]!;
    return chosen.statuses.length === 0
      ? items
      : items.filter((j) => chosen.statuses.includes(j.status));
  }, [jobs.data, filter]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['field', 'jobs'] });

  return (
    <div>
      <PageHeader
        title="Field jobs"
        description="Dispatch deployment work orders, follow them across the field, and close what the field could not finish."
        actions={
          <>
            <Button icon={<RefreshCw className="size-4" />} onClick={refresh} loading={jobs.isFetching}>
              Refresh
            </Button>
            {canManage && (
              <Button
                variant="primary"
                icon={<Plus className="size-4" />}
                disabled={engineersQuery.isLoading}
                onClick={() => setRaising(true)}
              >
                Raise a job
              </Button>
            )}
          </>
        }
      />

      {canManage && (
        <div className="mb-6 grid gap-6 lg:grid-cols-2">
          <GpsPolicyCard />
          {/* The steps a technician ticks at the wall (2026-09-16) — beside the policy they sit under. */}
          <ChecklistCard />
        </div>
      )}

      <Card
        title="Board"
        action={
          <div className="flex flex-wrap gap-1">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                  filter === f.key ? 'bg-primary-soft text-primary' : 'text-muted hover:text-ink'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        }
      >
        {jobs.isLoading ? (
          <div className="flex justify-center p-10">
            <Spinner />
          </div>
        ) : jobs.isError ? (
          <div className="p-5">
            <ErrorNotice error={jobs.error as Error} />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<ClipboardList className="size-6" />}
            title="No jobs here"
            description={
              filter === 'open'
                ? 'Nothing is out with the field right now.'
                : 'No jobs match this filter.'
            }
            action={
              canManage ? (
                <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setRaising(true)}>
                  Raise a job
                </Button>
              ) : undefined
            }
          />
        ) : (
          <Table>
            <THead>
              <th className="px-5 py-2.5 text-left text-xs font-medium text-muted">Job</th>
              <th className="px-5 py-2.5 text-left text-xs font-medium text-muted">Target</th>
              <th className="px-5 py-2.5 text-left text-xs font-medium text-muted">Assigned to</th>
              <th className="px-5 py-2.5 text-left text-xs font-medium text-muted">Status</th>
              <th className="px-5 py-2.5 text-right text-xs font-medium text-muted">Updated</th>
            </THead>
            <TBody>
              {rows.map((j) => (
                <TRow key={j.id} onClick={() => setOpenJob(j)}>
                  <td className="px-5 py-3 text-sm text-ink">{j.title}</td>
                  <td className="px-5 py-3 text-sm text-muted">{j.targetLabel}</td>
                  <td className="px-5 py-3 text-sm text-muted">{j.assignedToName ?? '—'}</td>
                  <td className="px-5 py-3">
                    <Pill state tone={STATUS_TONE[j.status]}>{STATUS_LABEL[j.status]}</Pill>
                  </td>
                  <td className="px-5 py-3 text-right text-xs text-faint">{formatRelative(j.updatedAt)}</td>
                </TRow>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      {raising && (
        <RaiseFieldJobDialog engineers={engineers} onClose={() => setRaising(false)} />
      )}
      {openJob && (
        <FieldJobDetailDialog
          job={openJob}
          engineers={engineers}
          canManage={canManage}
          canClose={canClose}
          onClose={() => setOpenJob(null)}
          onChanged={refresh}
        />
      )}
    </div>
  );
}
